/**
 * Pure match session of Tacada Limpa. No engine, no wall clock, no Math.random: the view feeds `step(dtMs)` (match time,
 * which stops while paused) and forwards the player's putt; the seed decides the round. The putt is resolved at once
 * (closed simulation); the view plays its path back while the clock runs, and the next putt waits until the ball stops.
 */
import { STEP_MS, simulate, type Pt, type PuttResult } from './physics'
import { applyPutt, isOver, isWin, newRound, roundOf, terminalScore, type RoundHole, type RoundState } from './rules'

/** Frame gaps longer than this (tab asleep, slow device) count as this much match time, never more. */
export const MAX_STEP_MS = 500

export interface PuttRecord { hole: number; angle: number; strength: number; atMs: number; from: Pt; result: PuttResult }

export class Session {
  readonly round: RoundHole[]
  state: RoundState = newRound()
  ball: Pt
  elapsedMs = 0
  readyAtMs = 0
  private carryMs = 0
  history: PuttRecord[] = []

  constructor(readonly levelId: string, readonly seed: number) {
    this.round = roundOf(levelId, seed)
    this.ball = { ...this.round[0].course.tee }
  }

  get over() { return isOver(this.state) }
  get won() { return isWin(this.state, this.round) }
  get score() { return terminalScore(this.state, this.round) }
  /** The hole being played (the last one stays after the end, for the view). */
  get hole(): RoundHole { return this.round[Math.min(this.state.hole, this.round.length - 1)] }
  get ready() { return !this.over && this.elapsedMs >= this.readyAtMs }

  /** Advances match time. It keeps running after the end (the view plays the last putt back on it); no putt is accepted then. */
  step(dtMs: number) {
    // whole milliseconds only: the clock is part of the rules (moving gates) and of the wire (elapsedMs is an integer)
    this.carryMs += Math.min(Math.max(dtMs, 0), MAX_STEP_MS)
    const whole = Math.floor(this.carryMs)
    this.carryMs -= whole
    this.elapsedMs += whole
  }

  /** Putt now: angle 0..999 (1/1000 of a turn), strength 1..1000. Null when the ball is still rolling or the round is over. */
  putt(angle: number, strength: number, record = false): PuttRecord | null {
    return this.puttAt(angle, strength, this.elapsedMs, record)
  }

  /** Same as putt() at match time `atMs` (bots and replay jump the clock; it never goes back). */
  puttAt(angle: number, strength: number, atMs: number, record = false): PuttRecord | null {
    if (this.over) return null
    this.elapsedMs = Math.max(this.elapsedMs, Math.trunc(atMs))
    if (this.elapsedMs < this.readyAtMs) return null
    const hole = this.state.hole
    const from = { ...this.ball }
    const result = simulate(this.round[hole].course, from, angle, strength, this.elapsedMs, record)
    this.state = applyPutt(this.state, this.round, result.stop, result.holed)
    this.readyAtMs = this.elapsedMs + result.steps * STEP_MS
    // next ball position: same hole → where it stopped; new hole → its tee
    this.ball = this.state.hole !== hole && !this.over ? { ...this.round[this.state.hole].course.tee } : { ...result.stop }
    const rec = { hole, angle, strength, atMs: this.elapsedMs, from, result }
    this.history.push(rec)
    return rec
  }
}
